/** @jsxImportSource theme-ui */

import { BdTooltip } from 'components/_controls/BdTooltip';
import { BepiCategoryDescription } from 'models/BepiModels';
import React, { useEffect, useState } from 'react';
import { isMobile } from 'utils/responsive';
import { useBreakpointIndex } from '@theme-ui/match-media';
import { BdTooltipBoundaries } from 'components/_controls/BdTooltip/BdTooltip';
import BepiTooltipContent from './BepiTooltipContent';

type BepiTooltipProps = {
  bepiCategoryDescription: BepiCategoryDescription;
};

const BepiTooltip: React.FC<BepiTooltipProps> = ({ bepiCategoryDescription }) => {
  const isMobileDevice = isMobile(useBreakpointIndex());
  const [tooltipBoundary, setTooltipBoundary] = useState<BdTooltipBoundaries>();
  const [mobileTooltipContentWidth, setMobileTooltipContentWidth] = useState<number>();

  const tooltipContentWidth = 400;
  const windowMargin = 5;
  const tooltipPaddingAndMargin = 22;
  const containerPadding = 32;

  const handleResize = () => {
    setMobileTooltipContentWidth(window.innerWidth - tooltipPaddingAndMargin - containerPadding);
    setTooltipBoundary({
      right: window.innerWidth - tooltipContentWidth / 2 - windowMargin,
      left: tooltipContentWidth / 2 - windowMargin,
    });
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const infoIconSvg = (
    <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8.11595 0C6.51076 0 4.94163 0.469192 3.60697 1.34824C2.27231 2.22729 1.23207 3.47672 0.617793 4.93853C0.00351652 6.40034 -0.157206 8.00887 0.155949 9.56072C0.469105 11.1126 1.24207 12.538 2.37711 13.6569C3.51214 14.7757 4.95827 15.5376 6.5326 15.8463C8.10694 16.155 9.73879 15.9965 11.2218 15.391C12.7048 14.7855 13.9723 13.7602 14.8641 12.4446C15.7559 11.129 16.2319 9.58225 16.2319 8C16.2319 5.87827 15.3768 3.84344 13.8548 2.34315C12.3327 0.842855 10.2684 0 8.11595 0V0ZM8.11595 15.1867C6.67396 15.1867 5.26435 14.7652 4.06538 13.9755C2.86641 13.1858 1.93193 12.0634 1.38011 10.7502C0.82828 9.43703 0.683897 7.99203 0.965215 6.59795C1.24653 5.20387 1.94092 3.92333 2.96056 2.91826C3.9802 1.91319 5.2793 1.22872 6.69358 0.951423C8.10786 0.674124 9.5738 0.816444 10.906 1.36039C12.2382 1.90433 13.3769 2.82546 14.178 4.0073C14.9792 5.18914 15.4068 6.57861 15.4068 8C15.4068 8.94377 15.2182 9.87829 14.8518 10.7502C14.4854 11.6221 13.9484 12.4144 13.2713 13.0817C12.5943 13.7491 11.7906 14.2784 10.906 14.6396C10.0215 15.0008 9.07339 15.1867 8.11595 15.1867Z"
        fill="#6B6D78"
      />
      <path
        d="M9.71802 4.0001C9.30191 3.71051 8.81276 3.53979 8.3045 3.50677C7.79638 3.46852 7.28765 3.57251 6.83686 3.80677C6.38335 4.03827 6.0033 4.38845 5.73844 4.81888C5.47357 5.24931 5.33413 5.74335 5.33541 6.24677C5.32792 6.30408 5.33291 6.36232 5.35005 6.41759C5.36718 6.47285 5.39607 6.52389 5.43479 6.56729C5.47351 6.61068 5.52117 6.64545 5.57459 6.66926C5.62801 6.69308 5.68596 6.70539 5.74459 6.70539C5.80322 6.70539 5.86118 6.69308 5.9146 6.66926C5.96802 6.64545 6.01568 6.61068 6.05439 6.56729C6.09311 6.52389 6.122 6.47285 6.13914 6.41759C6.15627 6.36232 6.16126 6.30408 6.15377 6.24677C6.15629 5.89233 6.25432 5.54492 6.43783 5.2401C6.62734 4.93943 6.89618 4.69517 7.21561 4.53343C7.53385 4.36949 7.89199 4.29578 8.25019 4.3205C8.6084 4.34521 8.95263 4.46739 9.24459 4.67343C9.54371 4.87298 9.77845 5.15295 9.92092 5.4801C10.0621 5.80558 10.1111 6.16267 10.063 6.51343C10.0138 6.86413 9.86622 7.19432 9.63686 7.46677C9.41384 7.74277 9.11601 7.95081 8.77793 8.06677C8.46535 8.17466 8.19456 8.37573 8.00309 8.64211C7.81162 8.90849 7.70896 9.22697 7.70933 9.55343V9.75343C7.70933 9.86129 7.75279 9.96473 7.83016 10.041C7.90753 10.1173 8.01247 10.1601 8.12189 10.1601C8.23131 10.1601 8.33624 10.1173 8.41361 10.041C8.49098 9.96473 8.53445 9.86129 8.53445 9.75343V9.55343C8.53249 9.39552 8.5823 9.2412 8.67648 9.11343C8.76632 8.98275 8.8969 8.88446 9.04846 8.83343C9.52467 8.66837 9.94736 8.38063 10.2726 8.0001C10.5891 7.61656 10.7948 7.15591 10.8678 6.66677C10.9465 6.16086 10.8806 5.64334 10.6776 5.17229C10.4745 4.70125 10.1422 4.29533 9.71802 4.0001Z"
        fill="#6B6D78"
      />
      <path
        d="M7.72498 11.2199C7.60606 11.2947 7.51384 11.4042 7.46121 11.5332C7.40625 11.6593 7.39208 11.799 7.42063 11.9332C7.44749 12.0681 7.516 12.1915 7.61677 12.2866C7.71497 12.3871 7.84272 12.4547 7.98199 12.4799H8.11725C8.20995 12.4783 8.30159 12.4602 8.38778 12.4266C8.51771 12.3765 8.62874 12.288 8.70566 12.1732C8.76833 12.0588 8.79869 11.9299 8.79358 11.7999C8.8053 11.6699 8.77804 11.5393 8.71516 11.4244C8.65229 11.3095 8.55657 11.2153 8.43989 11.1536C8.32322 11.0918 8.19071 11.0652 8.05881 11.0769C7.92691 11.0887 7.80143 11.1384 7.69793 11.2199H7.72498Z"
        fill="#6B6D78"
      />
    </svg>
  );

  if (isMobileDevice) {
    return (
      bepiCategoryDescription && (
        <div data-testid="bepi-tooltip-mobile" sx={{ cursor: 'pointer' }}>
          <BdTooltip
            position="bottom center"
            offset="0, 6"
            popperModifiers={{
              preventOverflow: {
                enabled: true,
                boundariesElement: 'window',
                padding: 16,
              },
            }}
            customPopup={
              <BepiTooltipContent
                bepiCategoryDescription={bepiCategoryDescription}
                width={mobileTooltipContentWidth}
              />
            }
          >
            {infoIconSvg}
          </BdTooltip>
        </div>
      )
    );
  }
  return (
    bepiCategoryDescription && (
      <div data-testid="bepi-tooltip" sx={{ cursor: 'pointer' }}>
        <BdTooltip
          key={window.innerWidth}
          dynamicPositioning
          dynamicTooltipOffset={{ right: '14, 6', left: '0', bottom: '-1, 6' }}
          tooltipBoundaries={tooltipBoundary}
          customPopup={
            <BepiTooltipContent
              bepiCategoryDescription={bepiCategoryDescription}
              width={tooltipContentWidth}
            />
          }
        >
          {infoIconSvg}
        </BdTooltip>
      </div>
    )
  );
};

export default BepiTooltip;
